import { useOrderStore } from "@/store/orders.store"
import { useAuthStore } from "@/store/auth.store"
import { getNextWeekday } from "@/helper/time-helper"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import moment from "moment"
import type OrderController from "@/controllers/order-controller"
import { notificationSnapshot } from "./notification-snapshot"
import { BaseModel } from "@/models/base.model"
import DataHelper from "@/helper/data-helper"
import type { BaseStoreFunctions } from "@/store/base-functions/base-store-functions"
import type { IBase } from "@/interfaces/api/base.interface"
import ModelManager from "@/services/model-manager"
import { mountWhenIdle } from "@/composables/performance/idle-mount"

export type CacheController = { getStoreFunctions(): BaseStoreFunctions<any, any> }

// Use the same store hydration path as HTTP, including managed relationship resolution.
export async function restoreCachedModels(
  controllers: CacheController[],
  collections: Record<string, IBase[]>
) {
  // The planning shows once the orders and the rows they reference exist. The other rows of the lookup
  // collections, most of the snapshot, follow one collection per idle period.
  const orders = ordersForDates(collections.Order ?? [], restoredOrderDates())
  const needed = referencedIds(orders, collections)
  const later: (() => Promise<unknown>)[] = []
  // The references a snapshot holds resolve as their collections are created in this same pass; one it
  // does not hold would only hit the resolver's 30 s timeout, so the restore does not wait for resolution.
  const whenFullyResolved = BaseModel.whenFullyResolved
  BaseModel.whenFullyResolved = async () => {}
  try {
    await Promise.all(
      controllers.map(async (controller) => {
        const store = controller.getStoreFunctions()
        const model = store.getModelType().className!
        const data = model === "Order" ? orders : collections[model]
        if (!data) return
        const now = model === "Order" ? data : data.filter((row) => needed.has(row.id!))
        if (now.length < data.length) {
          const rest = data.filter((row) => !needed.has(row.id!))
          later.push(() => {
            // A recovered change may have created one of these meanwhile.
            const manager = ModelManager.getInstance()
            const missing = rest.filter((row) => !manager.exists(row.id!))
            return store.createModel(DataHelper.convertDateStringsToMoment(missing))
          })
        }
        if (now.length) await store.createModel(DataHelper.convertDateStringsToMoment(now))
      })
    )
  } finally {
    BaseModel.whenFullyResolved = whenFullyResolved
  }
  for (const load of later) mountWhenIdle(() => void load(), "low")
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** The ids the rows reference, directly or through the referenced rows themselves. */
export function referencedIds(roots: IBase[], collections: Record<string, IBase[]>) {
  const byId = new Map<string, IBase>()
  for (const rows of Object.values(collections))
    for (const row of rows) if (row.id) byId.set(row.id, row)
  const ids = new Set<string>()
  const walk = (value: unknown) => {
    if (typeof value === "string") {
      if (!UUID.test(value) || ids.has(value)) return
      ids.add(value)
      walk(byId.get(value))
    } else if (value && typeof value === "object") {
      for (const child of Object.values(value)) walk(child)
    }
  }
  roots.forEach(walk)
  return ids
}

// The instruction columns show today and the next weekdays; those are the days whose orders get built.
export function restoredOrderDates(
  columns = useAuthStore().activeUser?.userSettings?.layoutColumnConfigs ?? [],
  today = moment().startOf("day")
) {
  const count = columns.filter(
    (column) => column.component === LayoutColumnComponents.INSTRUCTION_LIST
  ).length
  const dates: string[] = []
  for (let day = today; dates.length < count; day = getNextWeekday(day))
    dates.push(day.format("YYYY-MM-DD"))
  return dates
}

// Like the first load, limited to those days: unprepared, undated or empty orders and those with an
// instruction on one of them. Without an instruction column no order is built; other days load when opened.
export function ordersForDates(rows: IBase[], dates: string[]) {
  if (!dates.length) return []
  return rows.filter((order: any) => {
    const instructions: { date?: string | null }[] = order.instructions ?? []
    return (
      !order.isPrepared ||
      !instructions.length ||
      instructions.some((instruction) => !instruction.date || dates.includes(instruction.date))
    )
  })
}

/** Orders with an instruction on the day, as the per-day route returns them. */
export function ordersOnDay(rows: IBase[], day: string) {
  return rows.filter((order: any) =>
    (order.instructions ?? []).some((instruction: any) => instruction.date === day)
  )
}

/** A day loaded before is built from the cached rows: the live feed and the recovery kept them current. */
export function loadOrdersForDay(controller: OrderController, date: moment.Moment) {
  const day = date.format("YYYY-MM-DD")
  return notificationSnapshot.runStartupGroup(
    `orders:${day}`,
    ["Order"],
    () => controller.fetchOrdersForSingleDate(date),
    () => void restoreDay(date, day)
  )
}

async function restoreDay(date: moment.Moment, day: string) {
  const store = useOrderStore()
  if (store.dateIsAlreadyFetched(date)) return
  store.onInitiateRequestForDateRange(date, date)
  try {
    const rows = ordersOnDay(notificationSnapshot.rows("Order"), day)
    await store.pushNonExistentOrders(DataHelper.convertDateStringsToMoment(rows))
  } finally {
    store.onRequestForDateRangeCompleted(date, date)
  }
}

// Startup owns registration. Realtime updates reuse those same stores, including
// unmanaged collections (templates and appProducts) that have no socket handlers.
let startupStores: BaseStoreFunctions<any, any>[] = []
export function registerStartupControllers(controllers: CacheController[]) {
  startupStores = controllers.map((controller) => controller.getStoreFunctions())
  notificationSnapshot.setStartupRestorer(async () => {
    try {
      await notificationSnapshot.restoreCollections((collections) =>
        restoreCachedModels(controllers, collections)
      )
    } catch (error) {
      notificationSnapshot.clear()
      console.warn("[recovery] Restore failed; using HTTP startup", error)
    }
  })
}

export type StartupChange = { row: IBase; deleted: boolean }

/** Apply a model's changes in one go: the collections update once, not once per row. */
export async function applyStartupChanges(model: string, changes: StartupChange[]) {
  const stores = startupStores.filter(
    (store) => store.getModelType().className === model && store.getCollection()
  )
  for (const store of stores) {
    const collection = store.getCollection()!.value
    const updated: [string, BaseModel][] = []
    const created: IBase[] = []
    for (const { row, deleted } of changes) {
      const index = collection.findIndex((candidate) => candidate.id === row.id)
      if (deleted) {
        if (index !== -1) store.deleteModel(collection[index])
      } else if (index !== -1) updated.push([row.id!, store.updateModel(row)])
      else created.push(row)
    }
    await BaseModel.whenFullyResolved(updated.map(([, instance]) => instance))
    for (const [id, instance] of updated)
      collection.splice(
        collection.findIndex((candidate) => candidate.id === id),
        1,
        instance
      )
    if (created.length) await store.createModel(created)
  }
  return stores.length > 0
}

export function applyStartupChange(model: string, data: IBase, deleted: boolean) {
  return applyStartupChanges(model, [{ row: data, deleted }])
}

export function loadInitialOrders(controller: OrderController, firstTime: boolean) {
  if (!firstTime) return controller.fetchInitialOrders(false)
  return notificationSnapshot.runStartupGroup(
    "orders", // Not per day: the snapshot plus the changes since the last visit is today's set as well.
    ["Order"],
    () => controller.fetchInitialOrders(true),
    markRestoredOrderDates
  )
}

function markRestoredOrderDates() {
  const store = useOrderStore()
  // Only the restored days count as loaded; others fetch when opened. Balanced counters, no HTTP request.
  for (const date of restoredOrderDates()) {
    const day = moment(date)
    store.onInitiateRequestForDateRange(day, day)
    store.onRequestForDateRangeCompleted(day, day)
  }
}

export const baseModels = [
  "Person",
  "KnownBusinessEntityContact",
  "Machine",
  "Project",
  "ProjectGroup",
  "Holiday",
  "KnownBusinessEntity",
  "Location",
  "Product",
  "AvailabilityReason"
]
export const transactionModels = [
  "PurchaseItem",
  "SaleItem",
  "RentalIntakeItem",
  "RentalDispenseItem"
]
